// src/components/issues/DocumentationALaDeclaration.tsx — SCE Issues
// Champs FACULTATIFS du formulaire de déclaration (15/10) : le N° de plan d'un
// MECA, l'EDE d'un ELEC / AUTOM — selon le métier déjà choisi.
//
// Discret : un simple lien « + … (facultatif) » tant que rien n'est tapé ; le
// formulaire ne s'allonge pas pour celui qui n'a pas le document sous les yeux
// (il le renseignera depuis la fiche, carte Documentation). Pas de champ du
// tout quand l'EDE de la machine est déjà connue (`edeConnue`) : la fiche la
// reprend toute seule. Règles et format : utils/documentationEDE.ts.

import { useState, type CSSProperties } from 'react'
import { Plus } from 'lucide-react'
import { concerneEDE, type SaisieDocumentation } from '@/utils/documentationEDE'
import type { Metier } from '@/types/issue.types'

interface Props {
  metier: Metier | null
  /** L'EDE de la machine choisie est déjà connue : on ne la redemande pas. */
  edeConnue: boolean
  valeur: SaisieDocumentation
  onChange: (valeur: SaisieDocumentation) => void
  desactive: boolean
}

const STYLE_CHAMP: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  backgroundColor: 'var(--color-white)',
  border: '1px solid var(--color-border)',
  fontSize: 'var(--font-size-copy2)',
  fontFamily: 'inherit',
  minWidth: 0,
}

const STYLE_LIEN: CSSProperties = {
  minHeight: 44,
  padding: 0,
  border: 'none',
  background: 'transparent',
  color: 'var(--color-black)',
  fontSize: 'var(--font-size-copy2)',
  textDecoration: 'underline',
}

export function DocumentationALaDeclaration({ metier, edeConnue, valeur, onChange, desactive }: Props): JSX.Element | null {
  const [ouvert, setOuvert] = useState(false)

  const estPlan = metier === 'MECA'
  const estEde = metier !== null && concerneEDE(metier) && !edeConnue
  if (!estPlan && !estEde) return null

  const rempli = estPlan
    ? valeur.plan.trim() !== ''
    : valeur.ede.numero.trim() !== '' || valeur.ede.rev.trim() !== '' || valeur.ede.pages.trim() !== ''

  if (!ouvert && !rempli) {
    return (
      <section className="px-4 pb-3">
        <button type="button" onClick={() => setOuvert(true)} disabled={desactive} className="flex items-center gap-2" style={STYLE_LIEN}>
          <Plus size={16} />
          {estPlan ? 'N° de plan (facultatif)' : 'EDE, schéma électrique (facultatif)'}
        </button>
      </section>
    )
  }

  if (estPlan) {
    return (
      <section className="px-4 pb-3">
        <label htmlFor="decl-plan" style={{ display: 'block', fontSize: 'var(--font-size-copy2)', fontWeight: 500, marginBottom: 8 }}>
          N° de plan <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)' }}>— facultatif, un par ligne</span>
        </label>
        <textarea
          id="decl-plan"
          value={valeur.plan}
          onChange={(e) => onChange({ ...valeur, plan: e.target.value })}
          rows={2}
          placeholder="Un plan par ligne"
          autoFocus={!rempli}
          disabled={desactive}
          className="p-3 rounded-lg outline-none"
          style={{ ...STYLE_CHAMP, resize: 'vertical' }}
        />
      </section>
    )
  }

  const champ = (id: string, libelle: string, cle: 'numero' | 'rev' | 'pages', placeholder: string, largeur?: number): JSX.Element => (
    <div className="flex flex-col" style={{ gap: 4, minWidth: 0, ...(largeur ? { width: largeur, flexShrink: 0 } : { flex: '1 1 0' }) }}>
      <label htmlFor={id} style={{ fontSize: 'var(--font-size-info)', color: 'var(--color-text-secondary)' }}>{libelle}</label>
      <input
        id={id}
        value={valeur.ede[cle]}
        onChange={(e) => onChange({ ...valeur, ede: { ...valeur.ede, [cle]: e.target.value } })}
        placeholder={placeholder}
        autoFocus={cle === 'numero' && !rempli}
        disabled={desactive}
        className="h-12 px-3 rounded-lg outline-none"
        style={STYLE_CHAMP}
      />
    </div>
  )

  return (
    <section className="px-4 pb-3">
      <h3 style={{ fontSize: 'var(--font-size-copy2)', fontWeight: 500, marginBottom: 8 }}>
        EDE, schéma électrique <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)' }}>— facultatif</span>
      </h3>
      <div className="flex flex-col gap-3">
        <div className="flex gap-2">
          {champ('decl-ede-numero', 'N° EDE', 'numero', 'NVS50312-G')}
          {champ('decl-ede-rev', 'Rév.', 'rev', '01', 72)}
        </div>
        {champ('decl-ede-pages', 'Page(s)', 'pages', '12, 12-14 ou 12, 18')}
        <span style={{ fontSize: 'var(--font-size-info)', color: 'var(--color-text-secondary)' }}>
          La référence complète se colle telle quelle : « NVS50312-G-EDE Rev 01 ».
        </span>
      </div>
    </section>
  )
}
